/**
 * 깊이 깎기 — 실루엣이 못 보는 오목부를 뷰별 깊이맵으로 깎는다.
 *
 * 비주얼 헐은 실루엣 원뿔의 교집합이다. 실루엣 안쪽의 정보(가슴판 아래 배, 턱 밑, 팔과
 * 몸통 사이의 골)는 어느 실루엣에도 없으니 헐은 그곳을 **볼록하게 채운다**. Meshy 와의
 * 차이가 가장 크게 보이는 곳이 바로 이 "채워진" 느낌이다.
 *
 * 그래서 뷰마다 깊이맵(단안 추정: 휴리스틱 또는 Depth-Anything)을 받아 헐을 안쪽으로
 * 깎는다. 깊이맵은 상대값(어파인 불변)이라 절대 길이가 없다. 그래서 헐 자체를 눈금으로
 * 쓴다: 뷰 i 에서 헐을 래스터해 픽셀별 앞면 깊이 Q 와 두께 T 를 재고, 깊이맵을 실루엣 안
 * 백분위로 0..1 정규화한 D̂ 로
 *
 *     새 표면 S = Q − carve · (1 − D̂) · T
 *
 * 만큼 **안쪽으로만** 옮긴다. 가장 튀어나온 점(D̂=1)은 헐에 남고(실루엣 극점이 곧 그곳이다),
 * 가장 들어간 점은 두께의 carve 비율까지 들어간다. 더 깎지 않는 이유는 단안 깊이가
 * 원근·조명에 흔들리기 때문이다 — 캐릭터를 얇게 갈아 버리는 것보다 덜 깎는 편이 안전하다.
 *
 * 결과는 뷰별 "표면 깊이맵" S_i 로 돌려주고, 헐 빌더가 두 번째 패스에서 복셀 점유에
 * 곱한다: q = p·d_i 가 S_i(s,t) 보다 카메라 쪽이면 빈 공간.
 */

import type { Field } from '../types.ts';
import type { ViewImage } from './views.ts';
import { viewSampler } from './views.ts';

export interface ViewBasisLike { rx: number; rz: number; dx: number; dz: number; w: number }

export interface CarveMap {
  /** N×N, (s,t) 격자. 값 = 그 픽셀에서 허용되는 최대 q (카메라 쪽 경계). 실루엣 밖은 +Infinity. */
  surface: Float32Array;
  size: number;
}

/** 뷰 i 에서 헐 삼각형을 래스터해 픽셀별 [최대 q(앞면), 최소 q(뒷면)] 을 만든다. */
export function rasterizeDepth(
  positions: ArrayLike<number>,
  tri: ArrayLike<number>,
  b: ViewBasisLike,
  N: number,
): { near: Float32Array; far: Float32Array } {
  const near = new Float32Array(N * N).fill(-Infinity);
  const far = new Float32Array(N * N).fill(Infinity);
  const proj = (v: number): [number, number, number] => {
    const x = positions[v * 3]!, y = positions[v * 3 + 1]!, z = positions[v * 3 + 2]!;
    return [((x * b.rx + z * b.rz) / b.w + 0.5) * (N - 1), y * (N - 1), x * b.dx + z * b.dz];
  };
  for (let t = 0; t < tri.length; t += 3) {
    const [ax, ay, ad] = proj(tri[t]!), [bx, by, bd] = proj(tri[t + 1]!), [cx, cy, cd] = proj(tri[t + 2]!);
    const x0 = Math.max(0, Math.floor(Math.min(ax, bx, cx))), x1 = Math.min(N - 1, Math.ceil(Math.max(ax, bx, cx)));
    const y0 = Math.max(0, Math.floor(Math.min(ay, by, cy))), y1 = Math.min(N - 1, Math.ceil(Math.max(ay, by, cy)));
    const area = (bx - ax) * (cy - ay) - (cx - ax) * (by - ay);
    if (Math.abs(area) < 1e-9) continue;
    const inv = 1 / area;
    for (let py = y0; py <= y1; py++) {
      for (let px = x0; px <= x1; px++) {
        const w0 = ((bx - px) * (cy - py) - (cx - px) * (by - py)) * inv;
        const w1 = ((cx - px) * (ay - py) - (ax - px) * (cy - py)) * inv;
        const w2 = 1 - w0 - w1;
        if (w0 < -0.02 || w1 < -0.02 || w2 < -0.02) continue;
        const d = w0 * ad + w1 * bd + w2 * cd;
        const i = py * N + px;
        if (d > near[i]!) near[i] = d;
        if (d < far[i]!) far[i] = d;
      }
    }
  }
  return { near, far };
}

/** 실루엣 안 백분위 정규화 (p3..p97 → 0..1). */
function normalizeInside(values: Float32Array, inside: Uint8Array): Float32Array {
  const pool: number[] = [];
  for (let i = 0; i < values.length; i++) if (inside[i]) pool.push(values[i]!);
  const out = new Float32Array(values.length);
  if (pool.length < 16) return out.fill(1);
  pool.sort((a, b) => a - b);
  const lo = pool[Math.floor(pool.length * 0.03)]!;
  const hi = pool[Math.floor(pool.length * 0.97)]!;
  const span = Math.max(1e-6, hi - lo);
  for (let i = 0; i < values.length; i++) out[i] = Math.min(1, Math.max(0, (values[i]! - lo) / span));
  return out;
}

/**
 * 뷰 하나의 깎기 맵.
 * @param depth  뷰 이미지 해상도의 깊이 필드(1 = 가까움). 마스크 밖은 아무 값이어도 된다.
 * @param carve  두께 대비 최대 깎기 비율 0..1
 */
export function buildCarveMap(
  positions: ArrayLike<number>,
  tri: ArrayLike<number>,
  view: ViewImage,
  b: ViewBasisLike,
  depth: Field,
  carve: number,
  N = 192,
): CarveMap {
  const { near, far } = rasterizeDepth(positions, tri, b, N);
  const sampler = viewSampler(view);
  const inside = new Uint8Array(N * N);
  const dv = new Float32Array(N * N);
  for (let py = 0; py < N; py++) {
    for (let px = 0; px < N; px++) {
      const i = py * N + px;
      if (near[i] === -Infinity) continue;
      const s = px / (N - 1), t = py / (N - 1);
      if (sampler.sample(s, t) < 0.5) continue;
      // 깊이 필드는 뷰 이미지보다 작을 수 있다(추정은 512 로 줄여서 돈다) — 비율로 옮긴다.
      const [ix, iy] = sampler.toPixel(s, t);
      const xi = Math.min(depth.width - 1, Math.max(0, Math.round(ix * (depth.width / view.raster.width))));
      const yi = Math.min(depth.height - 1, Math.max(0, Math.round(iy * (depth.height / view.raster.height))));
      inside[i] = 1;
      dv[i] = depth.data[yi * depth.width + xi]!;
    }
  }
  const dn = normalizeInside(dv, inside);
  const surface = new Float32Array(N * N).fill(Infinity);
  for (let i = 0; i < N * N; i++) {
    if (!inside[i]) continue;
    const thickness = Math.max(0, near[i]! - far[i]!);
    // 전체를 (1−D̂) 비율로 깎으면 평균 픽셀도 두께의 절반·carve 만큼 들어가고, 여덟 뷰가
    // 겹치며 캐릭터가 통째로 얇아진다(실측: 부피 −41%). 그래서 **먼 쪽 40% 만** 깎는다 —
    // D̂ ≥ 0.6 은 그대로, 0 에서 최대. 오목부만 들어가고 평지는 남는다.
    const amt = Math.min(1, Math.max(0, (0.6 - dn[i]!) / 0.6));
    surface[i] = near[i]! - carve * amt * thickness;
  }
  // 3×3 최댓값으로 살짝 부풀린다 — 픽셀 경계의 계단이 복셀에 줄무늬로 새기지 않게.
  const dil = new Float32Array(N * N);
  for (let py = 0; py < N; py++) {
    for (let px = 0; px < N; px++) {
      let m = -Infinity;
      for (let oy = -1; oy <= 1; oy++) for (let ox = -1; ox <= 1; ox++) {
        const qx = px + ox, qy = py + oy;
        if (qx < 0 || qy < 0 || qx >= N || qy >= N) continue;
        const v = surface[qy * N + qx]!;
        if (v !== Infinity && v > m) m = v;
      }
      dil[py * N + px] = m === -Infinity ? Infinity : m;
    }
  }
  return { surface: dil, size: N };
}

/** 쌍선형 샘플. 실루엣 밖(Infinity)은 이웃 값으로 대신한다. */
export function sampleCarve(map: CarveMap, s: number, t: number): number {
  const N = map.size;
  const fx = Math.min(N - 1, Math.max(0, s * (N - 1)));
  const fy = Math.min(N - 1, Math.max(0, t * (N - 1)));
  const x0 = Math.floor(fx), y0 = Math.floor(fy);
  const x1 = Math.min(N - 1, x0 + 1), y1 = Math.min(N - 1, y0 + 1);
  const tx = fx - x0, ty = fy - y0;
  const g = (x: number, y: number) => map.surface[y * N + x]!;
  const a = g(x0, y0), b = g(x1, y0), c = g(x0, y1), d = g(x1, y1);
  // Infinity 가 섞이면 유한한 것들만 평균한다.
  const vals = [a, b, c, d], ws = [(1 - tx) * (1 - ty), tx * (1 - ty), (1 - tx) * ty, tx * ty];
  let sum = 0, wsum = 0;
  for (let i = 0; i < 4; i++) if (vals[i] !== Infinity) { sum += vals[i]! * ws[i]!; wsum += ws[i]!; }
  return wsum > 1e-6 ? sum / wsum : Infinity;
}
